import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  EditorState,
  ContentState,
  convertToRaw,
  convertFromHTML,
} from "draft-js";
import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { Edit } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/Index";
import draftToHtml from "draftjs-to-html";
import apiService from "../../../../service/Api";
import { toast } from "react-toastify";

interface NotesTabProps {
  streamId?: number;
}

interface StreamResponse {
  id: number;
  stream_evaluation_summary: string | null;
  title?: string;
  date?: string;
  address_one?: string;
  address_two?: string;
  suburb?: string;
  city?: string;
  booking?: {
    id: number;
  };
}

const createEditorState = (html: string) => {
  try {
    if (!html?.trim()) return EditorState.createEmpty();
    const blocksFromHTML = convertFromHTML(html);
    return EditorState.createWithContent(
      ContentState.createFromBlockArray(
        blocksFromHTML.contentBlocks,
        blocksFromHTML.entityMap,
      ),
    );
  } catch {
    return EditorState.createEmpty();
  }
};

const NotesTab: React.FC<NotesTabProps> = ({ streamId }) => {
const theme = useSelector((state: RootState) => state.theme);

const currentUser = useSelector((state: RootState) => state.auth.user);

const [sendingWhatsApp, setSendingWhatsApp] = useState(false);

  const [notesContent, setNotesContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [streamData, setStreamData] =useState<StreamResponse | null>(null);
  const [editorState, setEditorState] = useState<EditorState>(
    EditorState.createEmpty(),
  );

  useEffect(() => {
    if (!streamId) return;
    apiService
      .get<StreamResponse>(`/api/streams/stream/${streamId}/`)
      .then((res) => {
        const content = res.data.stream_evaluation_summary || null;
        setNotesContent(content);
        setStreamData(res.data);
        if (!content) setEditing(true);
      })
      .catch(() => toast.error("Failed to fetch notes"))
      .finally(() => setLoading(false));
  }, [streamId]);

  const handleEdit = () => {
    setEditorState(createEditorState(notesContent || ""));
    setEditing(true);
  };

  const handleSave = useCallback(async () => {
    try {
      setSaving(true);
      const html = draftToHtml(convertToRaw(editorState.getCurrentContent()));
      await apiService.patch(`/api/streams/stream/${streamId}/`, {
        stream_evaluation_summary: html,
      });
      setNotesContent(html);
      setEditing(false);
      toast.success(notesContent ? "Notes updated successfully" : "Notes saved successfully");
    } catch {
      toast.error("Failed to save notes. Please try again.");
    } finally {
      setSaving(false);
    }
  }, [editorState, notesContent, streamId]);
  
    const handleSendToWhatsApp = () => {
    try {
      if (!notesContent || !notesContent.trim()) {
        toast.error("No notes available to send");
        return;
      }

      setSendingWhatsApp(true);

      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = notesContent;

      const plainText =
        tempDiv.textContent || tempDiv.innerText || "";


      const formattedDate = streamData?.date
        ? new Date(streamData.date).toLocaleDateString(
            "en-GB",
            {
              day: "2-digit",
              month: "long",
              year: "numeric",
            }
          )
        : "";


      const formattedAddress = [
        streamData?.address_one,
        streamData?.address_two,
        streamData?.suburb,
        streamData?.city,
      ]
        .filter((v) => v && v !== "N/A")
        .join(", ");


      const baseUrl = window.location.origin;


      const streamLink =
        `${baseUrl}/stream-management/view/${streamData?.id}`;


      const bookingLink =
        streamData?.booking?.id
          ? `${baseUrl}/booking-management/view/${streamData.booking.id}`
          : "";


      const lines = [
        "*Stream Details*",
        "",
        `*Title:* ${streamData?.title ?? ""}`,
        `*Stream Date:* ${formattedDate}`,
        `*Service Address:* ${formattedAddress}`,
        "",
        "*Stream Link:*",
        streamLink,
      ];


      if (bookingLink) {
        lines.push(
          "",
          "*Booking Link:*",
          bookingLink
        );
      }


      lines.push(
        "",
        "*Event Notes*",
        "",
        plainText,
        "",
        "*Forwarded By:*",
        `${currentUser?.first_name ?? ""} ${currentUser?.last_name ?? ""}`,
        currentUser?.username ?? "",
        "",
        "Sent from MMS"
      );


      const message = lines.join("\n");


      const whatsappUrl =
        `https://wa.me/?text=${encodeURIComponent(
          message.normalize("NFC")
        )}`;


      window.open(
        whatsappUrl,
        "_blank",
        "noopener,noreferrer"
      );


      toast.success("WhatsApp opened successfully");

    } catch (error) {
      console.error(error);
      toast.error("Failed to open WhatsApp");
    } finally {
      setSendingWhatsApp(false);
    }
  };

  const toolbarOptions = useMemo(
    () => ({
      options: [
        "inline",
        "blockType",
        "list",
        "textAlign",
        "link",
        "image",
        "remove",
        "history",
      ],
      inline: { options: ["bold", "italic", "underline", "strikethrough"] },
      blockType: {
        options: ["Normal", "H1", "H2", "H3", "H4", "H5", "H6", "Blockquote"],
      },
      list: { options: ["unordered", "ordered"] },
      textAlign: { options: ["left", "center", "right", "justify"] },
    }),
    [],
  );

  if (loading) {
    return (
      <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (editing) {
    return (
      <Paper
        sx={{
          p: 3,
          mt: 2,
          backgroundColor: theme.primary_colors[800],
          boxShadow: 0,
          borderRadius: 2,
        }}
      >
        <Typography
          variant="h5"
          sx={{ mb: 2, color: theme.primary_colors[700], fontWeight: "bold" }}
        >
          {notesContent ? "Edit Event Notes" : "Write Event Notes"}
        </Typography>

        <Box
          sx={{
            ".rdw-editor-toolbar": {
              backgroundColor: theme.primary_colors[900],
              borderColor: theme.primary_colors[50],
              color: theme.primary_colors[700],
            },
            ".rdw-option-wrapper, .rdw-dropdown-wrapper": {
              backgroundColor: theme.primary_colors[900],
              color: theme.primary_colors[700],
              borderColor: theme.primary_colors[50],
            },
            ".rdw-option-wrapper:hover, .rdw-option-active": {
              backgroundColor: theme.primary_colors[600],
            },
            ".rdw-editor-main": {
              minHeight: "200px",
              backgroundColor: theme.primary_colors[900],
              color: theme.primary_colors[700],
              border: `1px solid ${theme.primary_colors[300]}`,
              borderRadius: "4px",
              padding: "8px",
            },
            ".DraftEditor-root": {
              color: theme.primary_colors[700],
              "& p, & h1, & h2, & h3, & h4, & h5, & h6": {
                color: theme.primary_colors[700],
              },
              "& a": { color: theme.primary_colors[10] },
              lineHeight: 1.7,
              fontSize: { xs: "1rem", md: "1.1rem" },
            },
          }}
        >
          <Editor
            editorState={editorState}
            onEditorStateChange={setEditorState}
            toolbar={toolbarOptions}
            placeholder="Start writing here..."
          />
        </Box>

        <Box sx={{ display: "flex", gap: 2, mt: 3, justifyContent: "flex-end" }}>
          {notesContent && (
            <Button
              variant="outlined"
              onClick={() => setEditing(false)}
              disabled={saving}
            >
              Cancel
            </Button>
          )}
          <Button
            variant="contained"
            sx={{
              backgroundColor: theme.primary_colors[500],
              "&:hover": { backgroundColor: theme.primary_colors[400] },
            }}
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {saving ? "Saving..." : notesContent ? "Update" : "Save"}
          </Button>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      sx={{
        p: 3,
        mt: 2,
        backgroundColor: theme.primary_colors[800],
        boxShadow: 0,
        borderRadius: 2,
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
      <Typography
        variant="h5"
        sx={{ color: theme.primary_colors[700], fontWeight: "bold" }}
      >
        Event Notes
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Button
            variant="contained"
            color="success"
            size="small"
            onClick={handleSendToWhatsApp}
            disabled={!notesContent || sendingWhatsApp}
            sx={{ mr: 1 }}
          >
            {sendingWhatsApp
              ? "Opening..."
              : "Send to WhatsApp"}
          </Button>

          <IconButton onClick={handleEdit} size="small">
            <Edit fontSize="small" />
          </IconButton>
        </Box>
  
      </Box>

      <Box
        sx={{ color: theme.primary_colors[700], lineHeight: 1.7 }}
        dangerouslySetInnerHTML={{ __html: notesContent! }}
      />
    </Paper>
  );
};

export default NotesTab;
